import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  TextField,
  Box,
  Button,
  Typography,
  IconButton,
  Slide,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MuiMultiSelectBottomSheet = ({
  label,
  options = [],
  onChange,
  value = [],
  error = false,
  helperText = "",
  required = false,
  bottomSheetHeight = "50vh",
  subTitle = "",
  disabled = false,
  disableHelperText = false,
  minWidthValue = 100
}) => {
  const [open, setOpen] = useState(false);
  const [localSelections, setLocalSelections] = useState(value);
  const [searchTerm, setSearchTerm] = useState("");
  const [labelWidth, setLabelWidth] = useState(null);

  const dialogContentRef = useRef(null);
  const labelRef = useRef(null);

  const filteredOptions = options.filter((opt) =>
    typeof opt.label === "string"
      ? opt.label.toLowerCase().includes(searchTerm.toLowerCase())
      : false
  );

  useEffect(() => {
    const valueAsArray = Array.isArray(value) ? value : [];
    if (!open && JSON.stringify(valueAsArray) !== JSON.stringify(localSelections)) {
      setLocalSelections(valueAsArray);
    }
  }, [value, open]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        if (dialogContentRef.current) {
          dialogContentRef.current.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (dialogContentRef.current) {
      const firstItem = dialogContentRef.current.querySelector("li:not([style*='display: none'])");
      if (firstItem) {
        firstItem.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [searchTerm, filteredOptions.length]);

  useEffect(() => {
    if (labelRef.current) {
      const width = labelRef.current.offsetWidth;
      setLabelWidth(width + 40); // Add padding and icon space
    }
  }, [label, value]);

  const handleOpen = () => {
    if (!disabled) {
      setOpen(true);
      setSearchTerm("");
      setLocalSelections(Array.isArray(value) ? [...value] : []);
    }
  };

  const handleClose = () => {
    if (onChange) {
      const validSelectedValueArray = (Array.isArray(localSelections) ? localSelections : []).filter(
        (val) => options.some((option) => option.value === val)
      );
      onChange(validSelectedValueArray);
    }
    setOpen(false);
    setSearchTerm("");
  };

  const handleOk = () => {
    if (onChange) {
      const validSelectedValueArray = (Array.isArray(localSelections) ? localSelections : []).filter(
        (val) => options.some((option) => option.value === val)
      );
      onChange(validSelectedValueArray);
    }
    setOpen(false);
    setSearchTerm("");
  };

  const handleToggle = (val) => {
    const updated = localSelections.includes(val)
      ? localSelections.filter((id) => id !== val)
      : [...localSelections, val];

    setLocalSelections(updated);
  };

  const handleKeyDown = (event) => {
    if (["ArrowUp", "ArrowDown", "Enter"].includes(event.key)) {
      event.preventDefault();
    }
    if (event.key === "Backspace") {
      setSearchTerm((prev) => prev.slice(0, -1));
    } else if (event.key === "Escape") {
      if (searchTerm) {
        setSearchTerm("");
      } else {
        // Escape now also triggers the "save and close" behavior
        handleClose();
      }
    } else if (event.key.length === 1 && event.key.match(/[a-z0-9 ]/i)) {
      setSearchTerm((prev) => prev + event.key);
    }
  };

  const selectedCount = value.length;
  const textFieldDisplayValue = selectedCount > 0 ? `${label} (${selectedCount})` : "";
  const textFieldLabel = selectedCount === 0 ? label : "";

  const resolvedHelperText = error
    ? helperText || "Dette feltet er påkrevd"
    : required && value.length === 0 && !open
      ? helperText || "Dette feltet er påkrevd"
      : " ";

  const resolvedError = error || (required && value.length === 0 && !open);

  return (
    <>
      <span
        ref={labelRef}
        style={{
          position: "absolute",
          visibility: "hidden",
          whiteSpace: "nowrap",
          fontSize: "14px",
          fontWeight: "500",
          letterSpacing: "0.1px",
          fontFamily: "Inter",
          color: 'var(--M3-sys-light-on-surface-variant, #3F4949)',
          textAlign: 'center',
          fontStyle: 'normal',
          lineHeight: '20px',
          letterSpacing: '0.1px'
        }}
      >
        {textFieldDisplayValue || label}
      </span>

      <TextField
        label={textFieldLabel}
        value={textFieldDisplayValue}
        onClick={handleOpen}
        fullWidth={false}
        sx={{
          width: 'auto',
          minWidth: minWidthValue,
          maxWidth: '100%',
          "& .MuiOutlinedInput-root": {
            height: '35px',
            borderRadius: "8px",
            padding: "0 8px",
            "& fieldset": { borderColor: "rgba(0, 0, 0, 0.23)" },
            "&:hover fieldset": { borderColor: "rgba(0, 0, 0, 0.5) !important" },
            "&.Mui-focused fieldset": { borderColor: "#00696D !important" },
            "&.Mui-disabled": {
              "& .MuiInputBase-input": {
                WebkitTextFillColor: "rgba(0, 0, 0, 0.38) !important",
                color: "rgba(0, 0, 0, 0.38) !important",
              },
              "& fieldset": {
                borderColor: "rgba(0, 0, 0, 0.12) !important",
              },
            },
          },
          "& .MuiInputBase-input": {
            padding: "0 4px",
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            fontSize: "14px",
            fontWeight: "500",
            lineHeight: "20px",
            letterSpacing: "0.1px",
            color: selectedCount > 0 ? "#191C1C" : "#3F4949",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            boxSizing: "border-box",
          },
          "& .MuiInputLabel-root": {
            transform: "translate(14px, 7px) scale(1)",
            color: "#3F4949",
            "&.Mui-focused": {
              color: "#191C1C",
            },
            "&.Mui-error": {
              color: "#d32f2f",
            },
            "&.Mui-disabled": {
              color: "rgba(0, 0, 0, 0.38) !important",
            },
          },
          "& .MuiInputLabel-shrink": {
            transform: "translate(14px, -9px) scale(0.75)",
            color: "#191C1C",
            "&.Mui-disabled": {
              color: "rgba(0, 0, 0, 0.38) !important",
            },
          },
        }}
        error={resolvedError}
        helperText={!disableHelperText && resolvedHelperText}
        required={required}
        disabled={disabled}
        InputLabelProps={{
          shrink: selectedCount > 0 || resolvedError || resolvedHelperText !== " "
        }}
        InputProps={{
          readOnly: true,
          endAdornment: <ArrowDropDownIcon />,
        }}
        FormHelperTextProps={{ sx: { margin: 0 } }}
      />

      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        PaperProps={{
          style: {
            borderRadius: "16px 16px 0 0",
            width: "100%",
            maxWidth: "600px",
            position: "fixed",
            bottom: 0,
            margin: 0,
            overflowY: "auto",
            backgroundColor: "#F2F4F4",
            height: bottomSheetHeight,
          },
        }}
      >
        <Box sx={{ textAlign: "center", padding: "16px" }}>
          <Box
            sx={{
              width: 32,
              height: 4,
              opacity: 0.4,
              backgroundColor: "#6F7979",
              borderRadius: 2,
              margin: "0 auto",
            }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", paddingTop: "8px" }}>
          <IconButton onClick={handleClose} sx={{ color: "#3F4949" }}>
            <CloseIcon />
          </IconButton>
          {subTitle && (
            <Typography
              sx={{
                marginLeft: "4px",
                fontSize: "22px",
                fontWeight: "400",
                lineHeight: "28px",
                fontFamily: "Inter",
                color: 'var(--M3-sys-light-on-surface, #191C1C)',
                fontStyle: 'normal',
              }}
            >
              {subTitle}
            </Typography>
          )}
        </Box>

        <DialogContent
          ref={dialogContentRef}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          sx={{ overflowY: "auto", flexGrow: 1, padding: 0 }}
        >
          <List>
            {filteredOptions.map((opt) => (
              <ListItem
                key={opt.value}
                button
                onClick={() => handleToggle(opt.value)}
                sx={{
                  height: 56,
                  paddingLeft: 2,
                  paddingRight: 2,
                  justifyContent: "flex-start",
                  alignItems: "center",
                  paddingTop: '32px'
                }}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={localSelections.includes(opt.value)}
                    sx={{
                      color: "#6F7979",
                      "&.Mui-checked": { color: "#00696D" },
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={opt.label}
                  sx={{
                    color: "#191C1C",
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontWeight: "400",
                    lineHeight: "24px",
                  }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>

        <Button
          variant="contained"
          onClick={handleOk}
          disabled={/*localSelections.length === 0 ||*/ disabled}
          sx={{
            height: "40px",
            backgroundColor: "#00696D",
            color: "#fff",
            borderRadius: "100px",
            margin: "16px auto",
            px: 3,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#005B60",
            },
            "&.Mui-disabled": {
              backgroundColor: "rgba(0, 0, 0, 0.12)",
              color: "rgba(0, 0, 0, 0.26)",
            },
            // Added marginBottom to create padding below the button
            marginBottom: "50px",
          }}
        >
          Bruk
        </Button>
      </Dialog>
    </>
  );
};

export default MuiMultiSelectBottomSheet;